// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// kinds.ts — the five kinds of tile, and the constants every other module counts against.
//
// A kind is a COLOUR **and** a GLYPH, and the glyph is not decoration. Five colours cannot be made
// mutually distinguishable by contrast: for n colours each ≥3:1 against the next you need a
// luminance span of at least 3^(n-1), and the maximum span any two sRGB colours can have is
// (1.0 + 0.05) / (0.0 + 0.05) = 21. n = 4 already needs 27. So the most colours that can ever be
// mutually 3:1 is THREE, and a "every tile reads against every other tile" gate is not a hard
// target, it is an impossible one. What we assert instead (contrast.test.ts) is every kind against
// every SURFACE it can sit on, and we carry identity redundantly in the glyph and in the 2px grout
// that stops any two tiles from ever abutting.

export type Kind = 0 | 1 | 2 | 3 | 4;

export const KINDS = 5;
export const CINNABAR = 0;
export const WOAD = 1;
export const VERDIGRIS = 2;
export const MARL = 3;
export const IRIS = 4;

export const KIND_NAMES: readonly string[] = ['Cinnabar', 'Woad', 'Verdigris', 'Marl', 'Iris'];

/** The shape drawn on every tile of this kind. Identity survives any colour vision. */
export const KIND_GLYPHS: readonly string[] = ['▲', '◆', '✚', '●', '✦'];

/** Twenty of each kind. The bag is counts, never an array — see rules.ts. */
export const TILES_PER_KIND = 20;
export const TOTAL_TILES = KINDS * TILES_PER_KIND;

/** Five courses, capacity 1..5. Course i lays into Mosaic row i. */
export const COURSES = 5;
export const courseCap = (i: number): number => i + 1;
/** 1 + 2 + 3 + 4 + 5 */
export const COURSE_SLOTS = 15;

/** The Mosaic is 5x5. */
export const SIDE = 5;
export const CELLS = SIDE * SIDE;

/** Four tiles to a rack, always. */
export const RACK_SIZE = 4;

/** Racks by player count. Tiles per player per round: 10.0 / 9.3 / 9.0 against 15 course slots. */
export const RACKS_FOR: Readonly<Record<number, number>> = { 2: 5, 3: 7, 4: 9 };
export const racksFor = (players: number): number => RACKS_FOR[players] ?? RACKS_FOR[2];

export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 4;

/**
 * The Spoil ladder, as positive magnitudes. Cumulative: 1, 2, 4, 6, 8, 11, 14.
 * An eighth item and beyond costs nothing further — the tile is simply lost.
 */
export const SPOIL_LADDER: readonly number[] = [1, 1, 2, 2, 2, 3, 3];
export const SPOIL_SLOTS = SPOIL_LADDER.length;

/** End-of-game bonuses. */
export const ROW_BONUS = 2;
export const COL_BONUS = 7;
export const KIND_BONUS = 10;

/** Total penalty for `items` things (tiles and the Trowel alike) on the Spoil. */
export function spoilPenalty(items: number): number {
  let p = 0;
  for (let i = 0; i < items && i < SPOIL_SLOTS; i++) p += SPOIL_LADDER[i];
  return p;
}

/** The printed Mosaic: cell (r,c) may only ever hold kind (c - r) mod 5. A Latin square. */
export const printedKind = (r: number, c: number): Kind => ((((c - r) % SIDE) + SIDE) % SIDE) as Kind;

/**
 * The inverse: where kind k lands in row r on a printed Mosaic.
 *
 * DERIVED FROM `printedKind` RATHER THAN WRITTEN OUT SEPARATELY, and precomputed so the search never
 * pays for the scan. As two closed forms these are two independent constants that nothing at runtime
 * forces to agree: the renderer draws its hint from one, the rules lay the tile with the other, and
 * changing either alone puts the ghost in a cell the tile will never reach. The mechanism audit
 * found exactly that — flipping `printedKind` on its own changed nothing the rules could see, which
 * is a blind spot in the SOURCE, not in the audit. One constant now, with one inverse.
 */
const PRINTED_COL: number[] = (() => {
  const t = new Array(SIDE * KINDS).fill(0);
  for (let r = 0; r < SIDE; r++) {
    for (let c = 0; c < SIDE; c++) t[r * KINDS + printedKind(r, c)] = c;
  }
  return t;
})();
export const printedCol = (r: number, k: number): number => PRINTED_COL[r * KINDS + k];
